import React, { memo, Component } from 'react';
import { CheckCircle2Icon, CircleIcon, CircleDashedIcon, ArrowRightIcon, RouteIcon, ListChecksIcon, AlertTriangleIcon, SparklesIcon, UserCheckIcon, HomeIcon, HandshakeIcon, ShieldCheckIcon, StarIcon, LayoutGridIcon, GitBranchIcon, FlaskConicalIcon, PackageIcon, HammerIcon, EyeOffIcon, ServerIcon, TargetIcon } from 'lucide-react';
interface ChangelogProps {
  navigate: (page: string) => void;
}
// ============================================================
// MACRO OVERVIEW — pilonii produsului între v28 și v132
// ============================================================
interface Pillar {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  intent: string;
  before: string;
  now: string;
  highlights: string[];
}
const PILLARS: Pillar[] = [{
  icon: UserCheckIcon,
  title: 'Onboarding & profil',
  intent: 'Reducem frecția de creare a unui profil complet și relevant, pentru ambele tipuri de utilizatori.',
  before: 'Un singur formular liniar, doar pentru chiriași, fără diferențiere clară de rol.',
  now: 'Wizard în 8 pași cu split pe rol (chiriaș / proprietar), email verification banner, edit modals per secțiune, și nudges contextuale pentru completare 100%.',
  highlights: ['Wizard onboarding pe pași cu progress bar', 'Roluri duale (chiriaș + proprietar) pe același cont', 'Aplicare separată pentru rol de proprietar', 'Profile completeness nudges & checklists', 'Edit profile / edit landlord profile modals']
}, {
  icon: HomeIcon,
  title: 'Anunț & marketplace',
  intent: 'Inversăm modelul clasic — chiriașul își publică cererea, proprietarul descoperă și aplică.',
  before: 'Profilul de chiriaș era singura entitate. Nu exista concept de „anunț activ" sau marketplace.',
  now: 'Chiriașul creează un anunț cu buget, oraș, timing, stil de viață. Anunțul are stări (activ / arhivat / snapshot) și se vede în marketplace cu filtre.',
  highlights: ['Concept de anunț activ vs. anunțuri istoric', 'Snapshot view (versiune înghețată a anunțului)', 'Marketplace cu filtre', 'Arhivare automată la moved-in', 'Re-onboarding accelerat din istoric']
}, {
  icon: HandshakeIcon,
  title: 'Ofertă & tranzacție',
  intent: 'Închidem cercul: de la match → ofertă → acceptare → moved-in, tot pe platformă.',
  before: 'Fără mecanism formal — contactul se făcea în afara platformei.',
  now: 'Proprietarul construiește o ofertă cu poze, preț, termeni. Chiriașul acceptă / refuză / contra-ofertă. Confirmarea moved-in declanșează arhivarea și cererea de review.',
  highlights: ['OfferCard cu status pill clar', 'OfferEditModal pentru construire ofertă', 'OfferActionDialog (accept / refuz / negociere)', 'OfferPhotoGrid + OfferListingPreview', 'MovedInModal — tranziția anunț activ → istoric']
}, {
  icon: ShieldCheckIcon,
  title: 'Trust & verificări',
  intent: 'Trust-ul e prerechizita conversiei. Reducem riscul perceput de ambele părți.',
  before: 'Nu existau dovezi vizibile că un utilizator e cine spune că este.',
  now: 'Trei niveluri de verificare (email, identitate, venit) afișate ca badge-uri publice. AuthGate la acțiuni protejate. Forțează crearea de cont la momentul potrivit.',
  highlights: ['Verificare email (cod 6 cifre)', 'Verificare identitate (modal dedicat)', 'Verificare venit (modal dedicat)', 'TrustBadgeRow pe profil public', 'AuthGateModal — conversie contextuală']
}, {
  icon: StarIcon,
  title: 'Reviews & istoric',
  intent: 'Reputație portabilă: ce ai construit într-o închiriere te ajută la următoarea.',
  before: 'Niciun feedback între părți. Fără memorie a interacțiunilor anterioare.',
  now: 'Cerere de review la sfârșit de contract prin link public (fără cont obligatoriu pentru recenzent). Review-uri afișate pe profil. Istoric vizibil pentru ambele roluri.',
  highlights: ['RequestReviewModal & PendingReviewRequest', 'Pagină ReviewSubmission accesibilă fără cont', 'ReviewCard pe profilul public', 'LandlordRentalHistory (chiriași anteriori)', 'AnuntHistoryCard (anunțuri anterioare ale chiriașului)']
}, {
  icon: LayoutGridIcon,
  title: 'Design system & fundație UI',
  intent: 'Bază de componente reutilizabile pentru iterare rapidă.',
  before: 'Componente ad-hoc, duplicate, stiluri inconsistente.',
  now: 'Un set complet de UI primitives (Button, Input, Card, Modal, Toggle, etc.), plus componente specializate pentru dashboard și marketplace. Cookie banner, footer, header și sidebar consolidate.',
  highlights: ['UI primitives (Button, Input, Select, Card, etc.)', 'Componente dashboard reutilizabile', 'Cookie consent + banner', 'Sidebar dashboard cu navigație role-aware', 'DemoPersonasSwitcher pentru testing rapid']
}];
// ============================================================
// FLOWS
// ============================================================
interface FlowStep {
  label: string;
  status: 'done' | 'partial' | 'pending';
}
interface FlowDef {
  name: string;
  actor: 'Chiriaș' | 'Proprietar' | 'Ambii';
  description: string;
  steps: FlowStep[];
}
const FLOWS: FlowDef[] = [{
  name: 'Onboarding chiriaș',
  actor: 'Chiriaș',
  description: 'Wizard în 8 pași care creează simultan contul, profilul și primul anunț.',
  steps: [{
    label: 'StepRole (alegere rol)',
    status: 'done'
  }, {
    label: 'StepAccount (email + parolă)',
    status: 'done'
  }, {
    label: 'StepProfile (date personale)',
    status: 'done'
  }, {
    label: 'StepListingType',
    status: 'done'
  }, {
    label: 'StepLocationBudget',
    status: 'done'
  }, {
    label: 'StepTiming',
    status: 'done'
  }, {
    label: 'StepBioAmenities',
    status: 'done'
  }, {
    label: 'StepLifestyle + StepLivingModel',
    status: 'done'
  }, {
    label: 'Verificare email post-onboarding',
    status: 'done'
  }]
}, {
  name: 'Onboarding proprietar',
  actor: 'Proprietar',
  description: 'Conversie din cont existent în rol de proprietar, sau cont nou cu rol direct.',
  steps: [{
    label: 'LandlordApplicationForm',
    status: 'done'
  }, {
    label: 'Adăugare primă proprietate',
    status: 'done'
  }, {
    label: 'Verificare email proprietar',
    status: 'done'
  }, {
    label: 'Verificare identitate proprietar',
    status: 'partial'
  }, {
    label: 'Validare proprietate (act, cadastru)',
    status: 'pending'
  }]
}, {
  name: 'Flow ofertă',
  actor: 'Ambii',
  description: 'Proprietarul trimite o ofertă pe profilul chiriașului, chiriașul răspunde, se semnează contract.',
  steps: [{
    label: 'Proprietar deschide profil chiriaș',
    status: 'done'
  }, {
    label: 'Construire ofertă (poze, preț, termeni)',
    status: 'done'
  }, {
    label: 'Notificare chiriaș',
    status: 'partial'
  }, {
    label: 'Accept / refuz / contra-ofertă',
    status: 'done'
  }, {
    label: 'Schimb mesaje pre-contract',
    status: 'pending'
  }, {
    label: 'Confirmare moved-in',
    status: 'done'
  }, {
    label: 'Generare contract digital',
    status: 'pending'
  }]
}, {
  name: 'Flow review',
  actor: 'Ambii',
  description: 'La final de chirie, fiecare parte poate solicita review-ul celeilalte printr-un link public.',
  steps: [{
    label: 'Inițiere RequestReviewModal',
    status: 'done'
  }, {
    label: 'Trimitere link prin email',
    status: 'partial'
  }, {
    label: 'Pagina ReviewSubmission (fără cont)',
    status: 'done'
  }, {
    label: 'Afișare ReviewCard pe profil public',
    status: 'done'
  }, {
    label: 'Moderare review-uri (admin)',
    status: 'pending'
  }, {
    label: 'Dispute / drept la replică',
    status: 'pending'
  }]
}, {
  name: 'Flow verificare trust',
  actor: 'Ambii',
  description: 'Email, identitate și venit — afișate ca badge-uri pe profilul public.',
  steps: [{
    label: 'Verificare email (cod 6 cifre)',
    status: 'done'
  }, {
    label: 'Verificare identitate (modal)',
    status: 'partial'
  }, {
    label: 'Verificare venit (modal)',
    status: 'partial'
  }, {
    label: 'Integrare provider KYC real',
    status: 'pending'
  }, {
    label: 'Integrare open banking pt. venit',
    status: 'pending'
  }]
}, {
  name: 'Marketplace & descoperire',
  actor: 'Proprietar',
  description: 'Browsing public al anunțurilor de chiriași, cu filtre și gate de autentificare la contact.',
  steps: [{
    label: 'Marketplace cu filtre',
    status: 'done'
  }, {
    label: 'Profil chiriaș public',
    status: 'done'
  }, {
    label: 'AuthGateModal la acțiuni protejate',
    status: 'done'
  }, {
    label: 'ApplicationWizardModal (aplicare la chiriaș)',
    status: 'done'
  }, {
    label: 'Salvare profile (DashboardProfileSalvate)',
    status: 'done'
  }, {
    label: 'Recomandări AI (match score)',
    status: 'pending'
  }]
}];
// ============================================================
// MVP READINESS AUDIT
// ============================================================
// Strategia: construim cât mai matur UX-ul în prototip, ascundem
// feature-urile prin feature flags până devin operaționale după
// implementarea backend-ului.
type MvpTier = 'blocker' | 'loophole' | 'post-mvp' | 'nice-to-have';
type PrototypeAction = 'build' | 'exists' | 'skip';
interface MvpItem {
  title: string;
  problem: string;
  proposal: string;
  tier: MvpTier;
  action: PrototypeAction;
  note?: string;
}
const MVP_ITEMS: MvpItem[] = [
// ---------- BLOCKERS ----------
{
  title: 'Mesagerie in-app',
  problem: 'Între ofertă acceptată și moved-in, comunicarea iese din platformă (WhatsApp). Tracking pierdut, fără moderare posibilă.',
  proposal: 'Thread per ofertă acceptată cu mesaje text + atașamente. Inbox separat în Sidebar. Implementare in-house (poll sau WebSocket simplu), fără third-party.',
  tier: 'blocker',
  action: 'build',
  note: 'UI complet acum cu mock data; backend simplu post-prototip.'
}, {
  title: 'Notificări in-app + email',
  problem: 'Nu există triggere pentru ofertă nouă, mesaj nou, cerere review, moved-in. Retention zero fără ele.',
  proposal: 'Bell icon în Header cu dropdown notificări + pagina dedicată „Notificări". Email-urile rămân backend după integrarea Resend.',
  tier: 'blocker',
  action: 'build',
  note: 'UI-ul de notificări in-app e prototipabil acum cu mock data.'
}, {
  title: 'Verificare identitate reală (KYC)',
  problem: 'Modalele actuale marchează contul ca verificat instant — promisiunea de trust e momentan falsă.',
  proposal: 'UI-ul există. Backend trebuie integrat cu Veriff / Onfido / Smart-ID. Putem extinde modalul cu states de „în review" și „respins" pentru a acoperi toate cazurile.',
  tier: 'blocker',
  action: 'build',
  note: 'Adăugăm states intermediare în VerifyIdentityModal (pending, rejected, expired).'
}, {
  title: 'Reset password — flow complet',
  problem: 'Paginile există dar fluxul nu se închide vizual (success/expired token, email trimis).',
  proposal: 'Stări de UI complete: „email trimis", „link expirat", „resetare reușită". Backend leagă tokenul real.',
  tier: 'blocker',
  action: 'build',
  note: 'Doar UI states lipsă, pagini deja create.'
}, {
  title: 'Cookie consent granular',
  problem: 'Linkul „Setări cookie-uri" din footer nu face nimic. Politica de confidențialitate promite consimțământ granular — momentan necompliant GDPR.',
  proposal: 'Modal cu toggle-uri pe categorii (esențiale / analitice / funcționale), persistat în localStorage, accesibil oricând din footer.',
  tier: 'blocker',
  action: 'build',
  note: 'Complet UI-only, fără dependență backend.'
}, {
  title: 'Self-service GDPR în Setări',
  problem: 'Politica promite drept la export și ștergere, dar nu există UI dedicat — cererile ajung pe email.',
  proposal: 'Secțiune nouă în DashboardSetari: „Datele mele" cu export JSON + „Șterge cont permanent" cu confirmare 14 zile.',
  tier: 'blocker',
  action: 'build'
},
// ---------- LOOPHOLES ----------
{
  title: 'Raportare utilizatori',
  problem: 'Un utilizator abuziv nu poate fi raportat. Risc reputațional și legal.',
  proposal: 'Buton „Raportează" în ActionMenu pe profil, ofertă și (viitor) mesaj. Modal cu motive predefinite + textarea.',
  tier: 'loophole',
  action: 'build'
}, {
  title: 'Block / unmatch',
  problem: 'Dacă cineva trimite oferte inadecvate repetat, cealaltă parte nu se poate apăra.',
  proposal: 'Acțiune „Blochează utilizator" în ActionMenu cu confirmare. Listă utilizatori blocați în Setări.',
  tier: 'loophole',
  action: 'build'
}, {
  title: 'Search + sortare în Marketplace',
  problem: 'Există doar filtre. La 200+ chiriași afișați, descoperirea cade.',
  proposal: 'Search bar (nume / oraș / cuvinte cheie) + dropdown sortare (recent, buget, match score când există).',
  tier: 'loophole',
  action: 'build'
}, {
  title: 'Preview profil înainte de publish',
  problem: 'Chiriașul nu vede cum apare profilul lui pentru proprietari.',
  proposal: 'Buton „Vezi cum te văd proprietarii" în DashboardProfil care deschide profilul public într-un modal sau pagină nouă.',
  tier: 'loophole',
  action: 'build'
}, {
  title: 'Save & resume onboarding',
  problem: 'Wizard-ul de 8 pași — abandonările sunt total pierdute.',
  proposal: 'Salvare draft per pas în localStorage + banner „Ai un onboarding neterminat" pe landing și email de reminder.',
  tier: 'loophole',
  action: 'build',
  note: 'UI banner + dialog „continuă unde ai rămas".'
}, {
  title: 'Verificare proprietate (proprietari)',
  problem: 'Oricine poate aplica ca proprietar fără să dovedească că deține imobilul.',
  proposal: 'Modal de upload extras CF / act / factură utilități în PropertyEditorModal, cu state „în verificare" și badge pe proprietate.',
  tier: 'loophole',
  action: 'build'
}, {
  title: 'Moderare review-uri & drept la replică',
  problem: 'Review-urile sunt afișate fără filtru. Risc de abuz și defăimare.',
  proposal: 'Buton „Raportează review" pe fiecare ReviewCard + posibilitate de răspuns public (1 reply per review).',
  tier: 'loophole',
  action: 'build'
},
// ---------- POST-MVP ----------
{
  title: 'Generare contract digital',
  problem: 'După acceptare ofertă, semnarea se face în exterior — risc operațional.',
  proposal: 'Template standard cu completare automată din ofertă + profile, preview + semnătură electronică.',
  tier: 'post-mvp',
  action: 'build',
  note: 'UI-ul de preview & flow contract poate fi construit; semnătură electronică reală e backend.'
}, {
  title: 'Dashboard analitic proprietar',
  problem: 'Proprietarul nu știe câți chiriași i-au văzut oferta, rata de accept sau timp mediu de răspuns.',
  proposal: 'Pagină nouă în dashboard cu metrice cheie (views, accept rate, avg response time) și grafice simple.',
  tier: 'post-mvp',
  action: 'build'
}, {
  title: 'Verificare venit prin open banking',
  problem: 'Verificarea voluntară de venit are credibilitate scăzută fără proof.',
  proposal: 'Integrare cu un provider open banking (Tink / Salt Edge). Backend-heavy.',
  tier: 'post-mvp',
  action: 'skip',
  note: 'Modal UI există; integrarea e pur backend.'
}, {
  title: 'Multi-anunț per chiriaș',
  problem: 'Un chiriaș care caută și studio și cameră trebuie să aleagă unul.',
  proposal: 'Permite max 2-3 anunțuri active simultan, cu indicator în Marketplace.',
  tier: 'post-mvp',
  action: 'build',
  note: 'Necesită investigare în date dacă cererea e reală.'
},
// ---------- NICE-TO-HAVE ----------
{
  title: 'Match score AI între profile și anunțuri',
  problem: 'Proprietarii scanează manual zeci de profile.',
  proposal: 'Scor de compatibilitate vizibil (0-100) bazat pe buget, timing, stil de viață.',
  tier: 'nice-to-have',
  action: 'build',
  note: 'UI-ul (badge pe card + breakdown în profil) e construibil acum cu mock score.'
}, {
  title: 'Localizare multi-oraș',
  problem: 'București / Cluj / Iași au dinamici diferite de piață.',
  proposal: 'Selector de oraș persistat + landing pages dedicate.',
  tier: 'nice-to-have',
  action: 'build'
}, {
  title: 'Mobile app (PWA → native)',
  problem: 'Trafic mobile >60% dar UX-ul mobile derivă din desktop.',
  proposal: 'PWA ca prim pas (install prompt, offline shell), apoi native dacă metricele justifică.',
  tier: 'nice-to-have',
  action: 'skip',
  note: 'PWA e mai mult infra; UX-ul mobile responsive e deja livrat.'
}];
const TIER_META: Record<MvpTier, {
  label: string;
  description: string;
  style: string;
  dotStyle: string;
}> = {
  blocker: {
    label: 'Blocker MVP',
    description: 'Fără ele, happy path-ul se rupe sau e necompliant.',
    style: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
    dotStyle: 'bg-rose-400'
  },
  loophole: {
    label: 'Loophole funcțional',
    description: 'UX-ul scârțâie sau există risc operațional.',
    style: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    dotStyle: 'bg-amber-400'
  },
  'post-mvp': {
    label: 'Post-MVP imediat',
    description: 'Lansăm fără, urmărim metricele, decidem după.',
    style: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
    dotStyle: 'bg-sky-400'
  },
  'nice-to-have': {
    label: 'Nice-to-have',
    description: 'Optimizări de impact pe termen lung.',
    style: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
    dotStyle: 'bg-violet-400'
  }
};
const ACTION_META: Record<PrototypeAction, {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  style: string;
}> = {
  build: {
    label: 'De construit în prototip',
    icon: HammerIcon,
    style: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
  },
  exists: {
    label: 'UI livrat',
    icon: CheckCircle2Icon,
    style: 'bg-dark-3 text-light-3 border-dark-4'
  },
  skip: {
    label: 'Backend-only — sărim',
    icon: ServerIcon,
    style: 'bg-dark-3 text-light-4 border-dark-4'
  }
};
// ============================================================
// SPRINT PACKAGES (organizarea pentru handoff dev)
// ============================================================
interface SprintPackage {
  id: string;
  title: string;
  goal: string;
  items: string[];
}
const SPRINT_PACKAGES: SprintPackage[] = [{
  id: 'P1',
  title: 'Compliance & legal',
  goal: 'Aducem platforma la conformitate GDPR și închidem flow-urile de cont.',
  items: ['Cookie consent granular (modal + persistență)', 'Self-service GDPR în Setări (export + șterge cont)', 'Reset password — toate states UI', 'Politici legale actualizate cu link în footer']
}, {
  id: 'P2',
  title: 'Trust & verificări',
  goal: 'Acoperim toate state-urile de UI pentru KYC, identitate, venit, proprietate.',
  items: ['States intermediare VerifyIdentityModal (pending, rejected, expired)', 'VerifyIncomeModal — states de upload + review', 'Verificare proprietate în PropertyEditorModal', 'Badge-uri „în review" pe profil public']
}, {
  id: 'P3',
  title: 'Comunicare & notificări',
  goal: 'UI complet pentru mesagerie in-app și sistem de notificări.',
  items: ['Pagină Mesagerie cu thread list + thread view', 'Bell icon în Header cu dropdown notificări', 'Pagină dedicată „Notificări"', 'Empty states + skeleton loaders']
}, {
  id: 'P4',
  title: 'Marketplace & descoperire',
  goal: 'Îmbunătățim descoperirea și conversia în Marketplace.',
  items: ['Search bar + sortare', 'Match score (UI cu mock)', 'Preview profil înainte de publish', 'Save & resume onboarding']
}, {
  id: 'P5',
  title: 'Siguranță & moderare',
  goal: 'Tools pentru protecția utilizatorilor.',
  items: ['Raportare utilizatori (profil, ofertă, mesaj, review)', 'Block / unmatch + listă utilizatori blocați', 'Drept la replică pe review-uri']
}, {
  id: 'P6',
  title: 'Tools pentru proprietar',
  goal: 'Funcționalități care cresc retention-ul proprietarilor.',
  items: ['Dashboard analitic (views, accept rate, response time)', 'Multi-property bulk actions', 'UI generare contract digital (cu disclaimer „în curând")']
}];
function StatusIcon({
  status
}: {
  status: FlowStep['status'];
}) {
  if (status === 'done') return <CheckCircle2Icon className="w-4 h-4 text-emerald-400 shrink-0" data-id="element-2858" />;
  if (status === 'partial') return <CircleDashedIcon className="w-4 h-4 text-amber-400 shrink-0" data-id="element-2859" />;
  return <CircleIcon className="w-4 h-4 text-light-4 shrink-0" data-id="element-2860" />;
}
export function Changelog({
  navigate
}: ChangelogProps) {
  const totalFlows = FLOWS.length;
  const totalMvpItems = MVP_ITEMS.length;
  const totalBlockers = MVP_ITEMS.filter(i => i.tier === 'blocker').length;
  const totalPackages = SPRINT_PACKAGES.length;
  const totalPillars = PILLARS.length;
  return <div className="min-h-screen bg-dark-1" data-id="element-2861">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-2862">
        {/* Header */}
        <div className="mb-12" data-id="element-2863">
          <div className="flex items-center gap-2 text-xs font-medium text-light-4 mb-4" data-id="element-2864">
            <GitBranchIcon className="w-3.5 h-3.5" data-id="element-2865" />
            <span className="uppercase tracking-wider" data-id="element-2866">
              Documentație internă · pentru echipa tehnică
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-light-1 mb-3" data-id="element-2867">
            De la v28 la v132 — overview produs
          </h1>
          <p className="text-light-3 text-base leading-relaxed max-w-2xl" data-id="element-2868">
            Un rezumat macro al ce s-a construit în ultimele ~100 de iterații.
            Document folosit pentru sincronizare între produs, design și echipa
            tehnică: ce flow-uri sunt definite, ce s-a livrat și ce a mai rămas.
          </p>
          <p className="text-sm text-light-4 mt-3" data-id="element-2869">
            Ultima actualizare: 14 mai 2026 · Versiune curentă:{' '}
            <span className="text-light-2 font-medium" data-id="element-2870">v132</span>
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-8" data-id="element-2871">
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/50" data-id="element-2872">
              <div className="text-2xl font-bold text-light-1" data-id="element-2873">
                {totalPillars}
              </div>
              <div className="text-xs text-light-4 mt-1" data-id="element-2874">piloni de produs</div>
            </div>
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/50" data-id="element-2875">
              <div className="text-2xl font-bold text-light-1" data-id="element-2876">
                {totalFlows}
              </div>
              <div className="text-xs text-light-4 mt-1" data-id="element-2877">flow-uri definite</div>
            </div>
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/50" data-id="element-2878">
              <div className="text-2xl font-bold text-light-1" data-id="element-2879">
                {totalBlockers}
                <span className="text-sm text-light-4 font-normal" data-id="element-2880">
                  {' / '}
                  {totalMvpItems}
                </span>
              </div>
              <div className="text-xs text-light-4 mt-1" data-id="element-2881">
                blockers MVP / total audit
              </div>
            </div>
          </div>
        </div>

        {/* Why this document */}
        <div className="border border-dark-4 rounded-lg p-5 bg-dark-2/30 mb-16" data-id="element-2882">
          <div className="flex items-start gap-3" data-id="element-2883">
            <SparklesIcon className="w-5 h-5 text-light-2 shrink-0 mt-0.5" data-id="element-2884" />
            <div className="space-y-2 text-sm leading-relaxed" data-id="element-2885">
              <p className="text-light-1 font-medium" data-id="element-2886">De ce acest document</p>
              <p className="text-light-3" data-id="element-2887">
                Produsul a iterat rapid de la v28 încoace. În loc de un istoric
                versiune cu versiune, am rezumat la nivel macro ce s-a construit
                pe fiecare pilon: cum arăta produsul la v28, cum arată acum, și
                ce feature-uri concrete au făcut diferența.
              </p>
              <p className="text-light-3" data-id="element-2888">Folosit pentru:</p>
              <ul className="list-disc pl-5 space-y-1 text-light-3" data-id="element-2889">
                <li data-id="element-2890">
                  Onboarding tehnic — un dev nou înțelege scope-ul în 10 minute
                </li>
                <li data-id="element-2891">Prioritizare — context clar pentru deciziile de roadmap</li>
                <li data-id="element-2892">
                  Aliniere produs/tehnic — fiecare pilon are un &quot;de
                  ce&quot;, nu doar un &quot;ce&quot;
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* v28 → v132 narrative */}
        <section className="mb-20" data-id="element-2893">
          <div className="border border-dark-4 rounded-lg overflow-hidden" data-id="element-2894">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-dark-4" data-id="element-2895">
              <div className="p-6 bg-dark-2/30" data-id="element-2896">
                <div className="flex items-center gap-2 mb-3" data-id="element-2897">
                  <span className="text-[10px] uppercase tracking-wider font-medium text-light-4 border border-dark-4 px-2 py-0.5 rounded" data-id="element-2898">
                    v28 — punct de pornire
                  </span>
                </div>
                <h3 className="text-base font-semibold text-light-1 mb-2" data-id="element-2899">
                  Concept brut, un singur rol
                </h3>
                <p className="text-sm text-light-3 leading-relaxed" data-id="element-2900">
                  Profil simplu de chiriaș, fără anunț, fără ofertă, fără
                  verificări. Proprietarii puteau doar să răsfoiască. Nu exista
                  marketplace, dashboard sau flow tranzacțional.
                </p>
              </div>
              <div className="p-6 bg-dark-2/30" data-id="element-2901">
                <div className="flex items-center gap-2 mb-3" data-id="element-2902">
                  <span className="text-[10px] uppercase tracking-wider font-medium text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 rounded" data-id="element-2903">
                    v132 — astăzi
                  </span>
                </div>
                <h3 className="text-base font-semibold text-light-1 mb-2" data-id="element-2904">
                  Platformă bidirecțională cu trust & istoric
                </h3>
                <p className="text-sm text-light-3 leading-relaxed" data-id="element-2905">
                  Roluri duale, wizard de onboarding cu split pe rol, anunțuri
                  cu stări, marketplace cu filtre, flow complet de ofertă →
                  moved-in → review, plus trei niveluri de verificare.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="mb-20" data-id="element-2906">
          <div className="flex items-center gap-2 mb-2" data-id="element-2907">
            <LayoutGridIcon className="w-4 h-4 text-light-2" data-id="element-2908" />
            <h2 className="text-lg font-semibold text-light-1" data-id="element-2909">
              Pilonii de produs construiți
            </h2>
          </div>
          <p className="text-sm text-light-3 mb-8 max-w-2xl" data-id="element-2910">
            Fiecare pilon grupează feature-urile pe direcția lor strategică, cu
            comparație înainte / acum și highlights concrete.
          </p>

          <div className="space-y-4" data-id="element-2911">
            {PILLARS.map(pillar => {
            const Icon = pillar.icon;
            return <div key={pillar.title} className="border border-dark-4 rounded-lg p-6 bg-dark-2/30" data-id="element-2912">
                  <div className="flex items-start gap-4" data-id="element-2913">
                    <div className="w-10 h-10 rounded-lg bg-dark-3 border border-dark-4 flex items-center justify-center shrink-0" data-id="element-2914">
                      <Icon className="w-5 h-5 text-light-2" data-id="element-2915" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2916">
                      <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2917">
                        {pillar.title}
                      </h3>
                      <p className="text-sm text-light-3 leading-relaxed mb-5" data-id="element-2918">
                        {pillar.intent}
                      </p>

                      {/* Before / Now */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-id="element-2919">
                        <div className="border border-dark-4 rounded-md p-3 bg-dark-1/50" data-id="element-2920">
                          <p className="text-[10px] uppercase tracking-wider text-light-4 font-medium mb-1.5" data-id="element-2921">
                            La v28
                          </p>
                          <p className="text-xs text-light-3 leading-relaxed" data-id="element-2922">
                            {pillar.before}
                          </p>
                        </div>
                        <div className="border border-emerald-500/20 rounded-md p-3 bg-emerald-500/[0.03]" data-id="element-2923">
                          <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-medium mb-1.5" data-id="element-2924">
                            La v132
                          </p>
                          <p className="text-xs text-light-2 leading-relaxed" data-id="element-2925">
                            {pillar.now}
                          </p>
                        </div>
                      </div>

                      {/* Highlights */}
                      <div data-id="element-2926">
                        <p className="text-[10px] uppercase tracking-wider text-light-4 font-medium mb-2" data-id="element-2927">
                          Ce s-a livrat concret
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" data-id="element-2928">
                          {pillar.highlights.map((h, idx) => <li key={idx} className="flex items-start gap-2 text-xs text-light-2" data-id="element-2929">
                              <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-2930" />
                              <span data-id="element-2931">{h}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </section>

        {/* Flows */}
        <section className="mb-20" data-id="element-2932">
          <div className="flex items-center gap-2 mb-2" data-id="element-2933">
            <RouteIcon className="w-4 h-4 text-light-2" data-id="element-2934" />
            <h2 className="text-lg font-semibold text-light-1" data-id="element-2935">
              Flow-uri definite
            </h2>
          </div>
          <p className="text-sm text-light-3 mb-8 max-w-2xl" data-id="element-2936">
            Fiecare flow are pași marcați cu status. Verde = livrat, galben =
            parțial (UI există, dar lipsește backend / integrare), gri = de
            construit.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2937">
            {FLOWS.map(flow => <div key={flow.name} className="border border-dark-4 rounded-lg p-5 bg-dark-2/30" data-id="element-2938">
                <div className="flex items-start justify-between gap-3 mb-2" data-id="element-2939">
                  <h3 className="text-base font-semibold text-light-1" data-id="element-2940">
                    {flow.name}
                  </h3>
                  <span className="text-[10px] uppercase tracking-wider text-light-4 border border-dark-4 px-2 py-0.5 rounded shrink-0" data-id="element-2941">
                    {flow.actor}
                  </span>
                </div>
                <p className="text-xs text-light-3 mb-4 leading-relaxed" data-id="element-2942">
                  {flow.description}
                </p>
                <ul className="space-y-1.5" data-id="element-2943">
                  {flow.steps.map((step, idx) => <li key={idx} className="flex items-center gap-2 text-xs text-light-2" data-id="element-2944">
                      <StatusIcon status={step.status} data-id="element-2945" />
                      <span className={step.status === 'pending' ? 'text-light-4' : ''} data-id="element-2946">
                        {step.label}
                      </span>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-6 text-xs text-light-4" data-id="element-2947">
            <span className="flex items-center gap-1.5" data-id="element-2948">
              <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-400" data-id="element-2949" />
              Livrat
            </span>
            <span className="flex items-center gap-1.5" data-id="element-2950">
              <CircleDashedIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-2951" />
              Parțial
            </span>
            <span className="flex items-center gap-1.5" data-id="element-2952">
              <CircleIcon className="w-3.5 h-3.5 text-light-4" data-id="element-2953" />
              De construit
            </span>
          </div>
        </section>

        {/* Strategy callout — prototype-first approach */}
        <section className="mb-20" data-id="element-2954">
          <div className="border border-violet-500/20 rounded-lg p-6 bg-violet-500/[0.04]" data-id="element-2955">
            <div className="flex items-start gap-3" data-id="element-2956">
              <FlaskConicalIcon className="w-5 h-5 text-violet-300 shrink-0 mt-0.5" data-id="element-2957" />
              <div className="space-y-3 text-sm leading-relaxed flex-1" data-id="element-2958">
                <p className="text-light-1 font-semibold" data-id="element-2959">
                  Strategie pe termen lung — prototype-first, hide-then-launch
                </p>
                <p className="text-light-3" data-id="element-2960">
                  Ceea ce construim acum este un{' '}
                  <span className="text-light-1 font-medium" data-id="element-2961">
                    prototip vizual matur
                  </span>
                  , nu o platformă funcțională. Obiectivul este să acoperim cât
                  mai multe flow-uri și cazuri UI/UX{' '}
                  <span className="text-light-1 font-medium" data-id="element-2962">acum</span>, astfel
                  încât echipa tehnică să aibă toate scenariile considerate la
                  momentul implementării backend-ului.
                </p>
                <p className="text-light-3" data-id="element-2963">Principii operaționale:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-light-3" data-id="element-2964">
                  <li data-id="element-2965">
                    <span className="text-light-2 font-medium" data-id="element-2966">
                      Construim UI pentru tot ce e UX-relevant
                    </span>{' '}
                    — inclusiv feature-uri care nu vor fi lansate inițial (ex.
                    contract digital, match score AI).
                  </li>
                  <li data-id="element-2967">
                    <span className="text-light-2 font-medium" data-id="element-2968">
                      Feature flags la implementare
                    </span>{' '}
                    — feature-urile ascunse pot fi activate gradual, fără
                    rework. Devs au tot UI-ul gata, doar comută flag-ul.
                  </li>
                  <li data-id="element-2969">
                    <span className="text-light-2 font-medium" data-id="element-2970">
                      Cost-awareness
                    </span>{' '}
                    — preferăm soluții in-house unde e posibil (ex. mesagerie
                    simplă fără third-party). Integrările cu cost (KYC,
                    semnătură electronică) rămân în UI dar marcate ca dependente
                    de backend.
                  </li>
                  <li data-id="element-2971">
                    <span className="text-light-2 font-medium" data-id="element-2972">
                      Pachete, nu task-uri izolate
                    </span>{' '}
                    — împărțim work-ul în pachete coerente care pot fi
                    construite și revizuite end-to-end.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* MVP Readiness Audit */}
        <section className="mb-20" data-id="element-2973">
          <div className="flex items-center gap-2 mb-2" data-id="element-2974">
            <TargetIcon className="w-4 h-4 text-light-2" data-id="element-2975" />
            <h2 className="text-lg font-semibold text-light-1" data-id="element-2976">
              Audit MVP — loopholes & priorități
            </h2>
          </div>
          <p className="text-sm text-light-3 mb-8 max-w-2xl" data-id="element-2977">
            Inventar al feature-urilor lipsă, grupate după impact. Fiecare item
            arată ce trebuie construit în prototip (UI) și ce rămâne pentru
            backend.
          </p>

          {/* Tier legend */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-2978">
            {(Object.keys(TIER_META) as MvpTier[]).map(tier => {
            const meta = TIER_META[tier];
            const count = MVP_ITEMS.filter(i => i.tier === tier).length;
            return <div key={tier} className="flex items-start gap-3 border border-dark-4 rounded-lg p-3 bg-dark-2/30" data-id="element-2979">
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${meta.dotStyle}`} data-id="element-2980" />
                  <div className="flex-1" data-id="element-2981">
                    <div className="flex items-center justify-between gap-2" data-id="element-2982">
                      <p className="text-sm font-medium text-light-1" data-id="element-2983">
                        {meta.label}
                      </p>
                      <span className="text-xs text-light-4 font-mono" data-id="element-2984">
                        {count}
                      </span>
                    </div>
                    <p className="text-xs text-light-3 mt-0.5 leading-relaxed" data-id="element-2985">
                      {meta.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* Items grouped by tier */}
          <div className="space-y-10" data-id="element-2986">
            {(Object.keys(TIER_META) as MvpTier[]).map(tier => {
            const items = MVP_ITEMS.filter(i => i.tier === tier);
            if (items.length === 0) return null;
            const meta = TIER_META[tier];
            return <div key={tier} data-id="element-2987">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2988">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dotStyle}`} data-id="element-2989" />
                    <h3 className="text-sm font-semibold text-light-1 uppercase tracking-wider" data-id="element-2990">
                      {meta.label}
                    </h3>
                    <span className="text-xs text-light-4" data-id="element-2991">
                      ({items.length})
                    </span>
                  </div>

                  <div className="space-y-3" data-id="element-2992">
                    {items.map((item, idx) => {
                  const actionMeta = ACTION_META[item.action];
                  const ActionIcon = actionMeta.icon;
                  return <div key={idx} className="border border-dark-4 rounded-lg p-5 bg-dark-2/30" data-id="element-2993">
                          <div className="flex items-start justify-between gap-3 mb-3" data-id="element-2994">
                            <h4 className="text-base font-semibold text-light-1" data-id="element-2995">
                              {item.title}
                            </h4>
                            <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded border ${actionMeta.style}`} data-id="element-2996">
                              <ActionIcon className="w-3 h-3" data-id="element-2997" />
                              {actionMeta.label}
                            </span>
                          </div>

                          <div className="space-y-2.5 text-xs leading-relaxed" data-id="element-2998">
                            <div data-id="element-2999">
                              <span className="text-light-4 font-medium uppercase tracking-wider text-[10px] mr-2" data-id="element-3000">
                                Problemă
                              </span>
                              <span className="text-light-3" data-id="element-3001">
                                {item.problem}
                              </span>
                            </div>
                            <div data-id="element-3002">
                              <span className="text-light-4 font-medium uppercase tracking-wider text-[10px] mr-2" data-id="element-3003">
                                Soluție
                              </span>
                              <span className="text-light-2" data-id="element-3004">
                                {item.proposal}
                              </span>
                            </div>
                            {item.note && <div className="border-l-2 border-dark-4 pl-3 mt-2" data-id="element-3005">
                                <span className="text-light-4 italic" data-id="element-3006">
                                  Notă: {item.note}
                                </span>
                              </div>}
                          </div>
                        </div>;
                })}
                  </div>
                </div>;
          })}
          </div>

          {/* Action legend */}
          <div className="flex flex-wrap items-center gap-4 mt-8 text-xs text-light-4 pt-6 border-t border-dark-4" data-id="element-3007">
            <span className="font-medium uppercase tracking-wider text-light-3" data-id="element-3008">
              Legendă acțiune:
            </span>
            <span className="flex items-center gap-1.5" data-id="element-3009">
              <HammerIcon className="w-3.5 h-3.5 text-emerald-300" data-id="element-3010" />
              UI de construit în prototip
            </span>
            <span className="flex items-center gap-1.5" data-id="element-3011">
              <CheckCircle2Icon className="w-3.5 h-3.5 text-light-3" data-id="element-3012" />
              UI deja livrat
            </span>
            <span className="flex items-center gap-1.5" data-id="element-3013">
              <ServerIcon className="w-3.5 h-3.5 text-light-4" data-id="element-3014" />
              Backend-only — sărim
            </span>
          </div>
        </section>

        {/* Sprint packages */}
        <section className="mb-20" data-id="element-3015">
          <div className="flex items-center gap-2 mb-2" data-id="element-3016">
            <PackageIcon className="w-4 h-4 text-light-2" data-id="element-3017" />
            <h2 className="text-lg font-semibold text-light-1" data-id="element-3018">
              Pachete de lucru — handoff structurat
            </h2>
          </div>
          <p className="text-sm text-light-3 mb-8 max-w-2xl" data-id="element-3019">
            Organizarea audit-ului în pachete coerente. Fiecare pachet livrează
            o secțiune end-to-end de UI gata de implementat, cu feature flag la
            launch.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3020">
            {SPRINT_PACKAGES.map(pkg => <div key={pkg.id} className="border border-dark-4 rounded-lg p-5 bg-dark-2/30 flex flex-col" data-id="element-3021">
                <div className="flex items-start gap-3 mb-3" data-id="element-3022">
                  <span className="shrink-0 w-8 h-8 rounded-md bg-dark-3 border border-dark-4 flex items-center justify-center text-xs font-mono font-semibold text-light-2" data-id="element-3023">
                    {pkg.id}
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-3024">
                    <h3 className="text-base font-semibold text-light-1" data-id="element-3025">
                      {pkg.title}
                    </h3>
                    <p className="text-xs text-light-3 mt-1 leading-relaxed" data-id="element-3026">
                      {pkg.goal}
                    </p>
                  </div>
                </div>
                <ul className="space-y-1.5 mt-1" data-id="element-3027">
                  {pkg.items.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-xs text-light-2" data-id="element-3028">
                      <CircleIcon className="w-3 h-3 text-light-4 shrink-0 mt-0.5" data-id="element-3029" />
                      <span data-id="element-3030">{item}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>

          <div className="mt-6 border border-dashed border-dark-4 rounded-lg p-4 bg-dark-2/20" data-id="element-3031">
            <div className="flex items-start gap-3" data-id="element-3032">
              <EyeOffIcon className="w-4 h-4 text-light-3 shrink-0 mt-0.5" data-id="element-3033" />
              <p className="text-xs text-light-3 leading-relaxed" data-id="element-3034">
                <span className="text-light-1 font-medium" data-id="element-3035">
                  Notă pentru dev:
                </span>{' '}
                fiecare pachet va fi livrat cu UI complet (inclusiv empty
                states, loading states, error states). La implementare,
                feature-urile sunt înfășurate în feature flags și expuse gradual
                pe măsură ce backend-ul e gata.
              </p>
            </div>
          </div>
        </section>

        {/* Caveats */}
        <section className="mb-20" data-id="element-3036">
          <div className="flex items-center gap-2 mb-2" data-id="element-3037">
            <AlertTriangleIcon className="w-4 h-4 text-amber-300" data-id="element-3038" />
            <h2 className="text-lg font-semibold text-light-1" data-id="element-3039">
              Caveats tehnice de știut
            </h2>
          </div>
          <p className="text-sm text-light-3 mb-6 max-w-2xl" data-id="element-3040">
            Decizii de produs care arată ca bug-uri dar sunt intenționate, sau
            datorii tehnice cunoscute.
          </p>
          <div className="space-y-3 text-sm" data-id="element-3041">
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/30" data-id="element-3042">
              <p className="text-light-1 font-medium mb-1" data-id="element-3043">
                Verificările sunt UI-only momentan
              </p>
              <p className="text-light-3 text-xs leading-relaxed" data-id="element-3044">
                Modalele de VerifyIdentity / VerifyIncome marchează contul ca
                verificat fără un provider real. E intenționat pentru beta —
                următorul pas e alegerea și integrarea unui provider KYC.
              </p>
            </div>
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/30" data-id="element-3045">
              <p className="text-light-1 font-medium mb-1" data-id="element-3046">
                Nu există încă sistem de mesagerie
              </p>
              <p className="text-light-3 text-xs leading-relaxed" data-id="element-3047">
                Comunicarea între ofertă și moved-in se face în afara
                platformei. Acceptăm pierderea de tracking pentru a livra restul
                flow-ului mai repede.
              </p>
            </div>
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/30" data-id="element-3048">
              <p className="text-light-1 font-medium mb-1" data-id="element-3049">
                DemoPersonasSwitcher e activ în toate environment-urile
              </p>
              <p className="text-light-3 text-xs leading-relaxed" data-id="element-3050">
                Permite schimbarea rapidă a persoanei pentru demo și testing.
                Trebuie ascuns în production înainte de launch public.
              </p>
            </div>
            <div className="border border-dark-4 rounded-lg p-4 bg-dark-2/30" data-id="element-3051">
              <p className="text-light-1 font-medium mb-1" data-id="element-3052">
                Datele de aplicare se păstrează 12 luni
              </p>
              <p className="text-light-3 text-xs leading-relaxed" data-id="element-3053">
                Conform Politicii de confidențialitate. Backend trebuie să
                implementeze un job de cleanup automat — momentan nu există.
              </p>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <div className="border-t border-dark-4 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-3054">
          <div data-id="element-3055">
            <p className="text-sm text-light-1 font-medium" data-id="element-3056">
              Întrebări sau propuneri?
            </p>
            <p className="text-xs text-light-3 mt-1" data-id="element-3057">
              Documentul se actualizează la fiecare release major. Pentru
              context suplimentar, scrie pe canalul tehnic intern.
            </p>
          </div>
          <button onClick={() => navigate('CONTACT')} className="inline-flex items-center gap-2 text-sm text-light-1 hover:text-light-2 transition-colors cursor-pointer group" data-id="element-3058">
            Contact echipă
            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-3059" />
          </button>
        </div>
      </div>
    </div>;
}